/**
 * Whose work the dashboard is showing.
 *
 * One page-level value that every widget obeys. It replaces two half-answers:
 * the old dashboard's recruiter multi-select, which defaulted to empty and so
 * showed everybody's data by accident, and this page's internal-only "viewing
 * as" employee picker, which was the same mechanism wearing a different hat.
 *
 * There are only two kinds. `TEAM` with an empty `employeeIds` is the whole
 * company, and that is not a convention layered on top — `buildFiltersQuery`
 * pushes no clause at all for an empty array, in applicants, bookings, tasks and
 * steps alike. So the cleared picker and the cleared query are the same state.
 *
 * Narrowing to exactly one person is still `TEAM`; the view collapses it back to
 * a personal agenda, because a one-row team is just a person.
 */
export type DashboardScope =
  | { kind: 'ME' }
  | { kind: 'TEAM'; employeeIds: string[] }

export const SCOPE_ME: DashboardScope = { kind: 'ME' }
export const SCOPE_TEAM: DashboardScope = { kind: 'TEAM', employeeIds: [] }

/** Roles that can own a booking or a task, and so can be scoped to. */
export const SCOPABLE_ROLE_TYPES = ['RECRUITER', 'ADMIN', 'OPS'] as const

/**
 * The employee ids to filter by, or `null` for "no clause — everyone".
 *
 * `null` and `[]` are deliberately different here even though the services treat
 * them alike, because a caller passing `[]` has usually lost a value somewhere.
 */
export const scopeEmployeeIds = (
  scope: DashboardScope,
  myEmployeeId: string | null | undefined
): string[] | null => {
  if (scope.kind === 'ME') return myEmployeeId ? [myEmployeeId] : null
  return scope.employeeIds.length > 0 ? scope.employeeIds : null
}

/**
 * The single employee this scope resolves to, if any — either `ME`, or a team
 * narrowed to one person. Drives the two places that behave per-person: hiding
 * the owner on every row, and naming the scope in the control.
 */
export const scopeSoloEmployeeId = (
  scope: DashboardScope,
  myEmployeeId: string | null | undefined
): string | null => {
  if (scope.kind === 'ME') return myEmployeeId ?? null
  return scope.employeeIds.length === 1 ? scope.employeeIds[0] : null
}

/**
 * Whether rows should name their owner. Only a scope covering more than one
 * person needs it; under a solo scope the name would be on every row, identical,
 * saying nothing the control does not already say.
 */
export const scopeShowsOwner = (
  scope: DashboardScope,
  myEmployeeId: string | null | undefined
): boolean => scopeSoloEmployeeId(scope, myEmployeeId) === null

/**
 * Whether the fetched page of employees covers every employee in the company.
 *
 * Pruning a remembered scope is only safe against a complete list: the employee
 * query is capped, so on a large company anyone past the first page is absent
 * while being perfectly active, and pruning against that partial list would
 * strip real people from a saved selection.
 */
export const isEmployeeListComplete = (
  fetchedCount: number | null | undefined,
  totalCount: number | null | undefined
): boolean =>
  typeof fetchedCount === 'number' &&
  typeof totalCount === 'number' &&
  fetchedCount >= totalCount

/**
 * Drops employee ids that are no longer selectable.
 *
 * A remembered scope outlives the people in it: archive an employee and their id
 * stays in `localStorage`, keeps narrowing every query, and yet vanishes from
 * the picker — so the results are filtered by somebody the user cannot see or
 * untick. Reconciling against the loaded list removes them.
 *
 * If that empties the selection the scope becomes the whole team, which is a
 * widening the user did not ask for — but the alternative is filtering on people
 * who no longer exist and staring at an empty page. The control says `Team`
 * either way, so the state is visible rather than silent.
 */
export const pruneScopeToKnownEmployees = (
  scope: DashboardScope,
  knownEmployeeIds: string[]
): DashboardScope => {
  if (scope.kind === 'ME' || scope.employeeIds.length === 0) return scope
  const known = new Set(knownEmployeeIds)
  const kept = scope.employeeIds.filter((id) => known.has(id))
  if (kept.length === scope.employeeIds.length) return scope
  return { kind: 'TEAM', employeeIds: kept }
}

export const isSameScope = (a: DashboardScope, b: DashboardScope): boolean => {
  if (a.kind !== b.kind) return false
  if (a.kind === 'ME' || b.kind === 'ME') return true
  const other = b as { kind: 'TEAM'; employeeIds: string[] }
  return (
    a.employeeIds.length === other.employeeIds.length &&
    a.employeeIds.every((id, index) => id === other.employeeIds[index])
  )
}

/**
 * A recruiter opens on their own work; an admin or ops user opens on the team.
 *
 * Both people who arrive here wanting the team view — us switching into a client
 * account, and a manager looking over their recruiters — hold an ADMIN employee
 * row, so the role is the signal already available. It is the same check
 * `AdminContainer` makes to show the entry point at all.
 *
 * A client can grant themselves ADMIN through Settings → Users, so "admin" means
 * admin *at this company*. For a default scope that is correct rather than a
 * leak: their own company's team state is exactly what they should see.
 */
export const defaultScopeForRole = (
  baseRole: string | null | undefined
): DashboardScope =>
  baseRole === 'ADMIN' || baseRole === 'OPS' ? SCOPE_TEAM : SCOPE_ME

const storageKey = (employeeId: string) =>
  `dn.agendaDashboard.scope.${employeeId}`

/**
 * Scope is remembered per employee, in `localStorage`.
 *
 * Deliberately not `ApplicantFilterPreset`, despite it being the codebase's
 * per-employee-JSON precedent: its `filters` column is documented as reflecting
 * `ApplicantFiltersInput`, and it carries columns, sort order, favourites and
 * sharing that a single enum plus a list of ids has no use for. If this ever
 * needs to follow a user between devices it wants its own small model, not a
 * lodger in that table.
 *
 * A narrowed team persists too. That reverses an earlier instinct — leave it on
 * a colleague and you would come back to their dashboard — but the control now
 * permanently shows a name or a row of faces, so there is nothing to be confused
 * by, and a manager watching the same people would otherwise re-pick them every
 * morning.
 */
export const readStoredScope = (
  employeeId: string | null | undefined
): DashboardScope | null => {
  if (!employeeId || typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(storageKey(employeeId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.kind === 'ME') return SCOPE_ME
    if (parsed?.kind === 'TEAM') {
      const ids = Array.isArray(parsed.employeeIds)
        ? parsed.employeeIds.filter((id: unknown) => typeof id === 'string')
        : []
      return { kind: 'TEAM', employeeIds: ids }
    }
    return null
  } catch {
    // Private browsing, a quota error, or hand-edited JSON. A remembered scope
    // is a convenience; failing to read one should never block the page.
    return null
  }
}

export const writeStoredScope = (
  employeeId: string | null | undefined,
  scope: DashboardScope
): void => {
  if (!employeeId || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey(employeeId), JSON.stringify(scope))
  } catch {
    // Same reasoning as above — the page works fine without persistence.
  }
}
